import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { useAnalytics } from './hooks/useAnalytics';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import LoadboardPage from './pages/LoadboardPage';
import LoadDetailPage from './pages/LoadDetailPage';
import PostLoadPage from './pages/PostLoadPage';
import ProfilePage from './pages/ProfilePage';
import DocumentsPage from './pages/DocumentsPage';
import BacklotPage from './pages/BacklotPage';
import ForumPostDetailPage from './pages/ForumPostDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import PricingPage from './pages/PricingPage';
import AdminDashboard from './pages/AdminDashboard';

// Driver Pages
import DriverDashboard from './pages/DriverDashboard';
import InspectionPage from './pages/InspectionPage';
import PhotoUploadPage from './pages/PhotoUploadPage';
import BOLPage from './pages/BOLPage';
import LoadTrackingPage from './pages/LoadTrackingPage';
import SecuritySettingsPage from './pages/SecuritySettingsPage';
import IntelMapPage from './pages/IntelMapPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';

// Legal/Info Pages
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CompanyDirectoryPage from './pages/CompanyDirectoryPage';
import AlertsPage from './pages/AlertsPage';
import DriverManagementPage from './pages/DriverManagementPage';
import DispatchBoardPage from './pages/DispatchBoardPage';
import ShipmentTrackingPage from './pages/ShipmentTrackingPage';
import InvoicesPage from './pages/InvoicesPage';

// Emergency SOS Page (No login required)
import SOSPage from './pages/SOSPage';

// Driver App Pages
import DriverLoginPage from './pages/driver/DriverLoginPage';
import DriverDashboardPage from './pages/driver/DriverDashboard';
import DriverLoadDetail from './pages/driver/DriverLoadDetail';
import DriverExpensesPage from './pages/driver/DriverExpensesPage';
import DriverDocumentsPage from './pages/driver/DriverDocumentsPage';
import DriverBOLPage from './pages/driver/DriverBOLPage';
import DriverNotificationsPage from './pages/driver/DriverNotificationsPage';
import { DriverNotificationProvider } from './context/DriverNotificationContext';

import './App.css';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useLayoutEffect(() => {
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [pathname]);
  
  return null;
}

// Analytics tracker component
function AnalyticsTracker() {
  useAnalytics();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <Routes>
            {/* Driver App Routes - Separate layout without main Navbar */}
            <Route path="/driver/login" element={<DriverLoginPage />} />
            <Route path="/driver/*" element={
              <DriverNotificationProvider>
                <Routes>
                  <Route path="/dashboard" element={<DriverDashboardPage />} />
                  <Route path="/load/:loadId" element={<DriverLoadDetail />} />
                  <Route path="/expenses" element={<DriverExpensesPage />} />
                  <Route path="/documents" element={<DriverDocumentsPage />} />
                  <Route path="/bol/:loadId" element={<DriverBOLPage />} />
                  <Route path="/notifications" element={<DriverNotificationsPage />} />
                </Routes>
              </DriverNotificationProvider>
            } />
            
            {/* Emergency SOS Page - Standalone, No Navbar, No Login Required */}
            <Route path="/sos" element={<SOSPage />} />
            <Route path="/emergency" element={<SOSPage />} />
            
            {/* Main App Routes with Navbar */}
            <Route path="/*" element={
              <div className="min-h-screen bg-background flex flex-col">
                <Navbar />
                <main className="flex-1">
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/loadboard" element={<LoadboardPage />} />
                    <Route path="/loads/:loadId" element={<LoadDetailPage />} />
                    <Route path="/backlot" element={<BacklotPage />} />
                    <Route path="/backlot/:postId" element={<ForumPostDetailPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/directory" element={<CompanyDirectoryPage />} />
                    <Route path="/alerts" element={<AlertsPage />} />
                    <Route path="/tms/dispatch" element={<DispatchBoardPage />} />
                    <Route path="/tms/drivers" element={<DriverManagementPage />} />
                    <Route path="/tms/invoices" element={<InvoicesPage />} />
                    <Route path="/track/:trackingToken" element={<ShipmentTrackingPage />} />
                    <Route path="/intel-map" element={<IntelMapPage />} />
              <Route path="/profile/:userId" element={<ProfilePage />} />
              
              {/* Legal/Info Pages */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              
              {/* Auth Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
              
              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/post-load"
                element={
                  <ProtectedRoute>
                    <PostLoadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-loads"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-requests"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assigned-loads"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <DocumentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/security"
                element={
                  <ProtectedRoute>
                    <SecuritySettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription/success"
                element={
                  <ProtectedRoute>
                    <PricingPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Legacy Driver Tools Routes (Carrier-side) - Renamed to avoid conflict */}
              <Route
                path="/tools/driver"
                element={
                  <ProtectedRoute>
                    <DriverDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/driver/inspection/:loadId"
                element={
                  <ProtectedRoute>
                    <InspectionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/driver/photos/:loadId"
                element={
                  <ProtectedRoute>
                    <PhotoUploadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/driver/bol/:loadId"
                element={
                  <ProtectedRoute>
                    <BOLPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/driver/load/:loadId"
                element={
                  <ProtectedRoute>
                    <LoadDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* GPS Tracking Routes */}
              <Route
                path="/track/:loadId"
                element={<LoadTrackingPage />}
              />
              <Route
                path="/tracking/:loadId"
                element={
                  <ProtectedRoute>
                    <LoadTrackingPage />
                  </ProtectedRoute>
                }
              />
                  </Routes>
                </main>
                <Footer />
              </div>
            } />
          </Routes>
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: 'hsl(240 6% 9%)',
              border: '1px solid hsl(240 4% 16%)',
              color: 'hsl(0 0% 98%)',
            },
          }}
        />
      </BrowserRouter>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
